import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'

type TokenGetter = () => string | null
type TokenSetter = (token: string | null) => void
type ClientIdGetter = () => string | null

let getAccessToken: TokenGetter = () => null
let setAccessToken: TokenSetter = () => {}
let getCurrentClientId: ClientIdGetter = () => null
let onSessionLost: () => void = () => {}

export function configureApi(opts: {
  getAccessToken: TokenGetter
  setAccessToken: TokenSetter
  getCurrentClientId?: ClientIdGetter
  onSessionLost?: () => void
}) {
  getAccessToken = opts.getAccessToken
  setAccessToken = opts.setAccessToken
  if (opts.getCurrentClientId) getCurrentClientId = opts.getCurrentClientId
  if (opts.onSessionLost) onSessionLost = opts.onSessionLost
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
})

// Public client for unauthenticated endpoints (QR landing scan, etc.).
// Intentionally has no request interceptor so JWT and X-Client-Id are
// never leaked to public routes. `withCredentials` is on so the
// HttpOnly anonymous tracking cookie survives.
export const publicApiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15_000,
})

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) config.headers.Authorization = `Bearer ${token}`

  // Inject X-Client-Id on client-scoped requests.
  // Backend endpoints that use get_current_client need this header.
  // Currently applies to nested client routes where a current client is selected.
  const clientId = getCurrentClientId()
  if (clientId && config.url && needsClientHeader(config.url)) {
    config.headers['X-Client-Id'] = clientId
  }

  return config
})

function needsClientHeader(url: string): boolean {
  // GET /clients/{id}/members — matches /clients/<uuid>/...
  return /\/clients\/[^/]+\/.+/.test(url)
}

// ── Session bootstrap ─────────────────────────────────────────────
// Call on app boot to exchange the HttpOnly refresh cookie for an access
// token (which lives only in memory). Without this, a page reload looks
// "logged out" even though the refresh cookie is still valid.
export async function bootstrapSession(): Promise<boolean> {
  try {
    const { data } = await axios.post<{ access_token: string }>(
      `${apiClient.defaults.baseURL}/auth/refresh`,
      {},
      { withCredentials: true },
    )
    setAccessToken(data.access_token)
    return true
  } catch {
    setAccessToken(null)
    return false
  }
}

// ── Silent refresh ────────────────────────────────────────────────
let refreshInFlight: Promise<string | null> | null = null

function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight

  const promise = axios
    .post<{ access_token: string }>(
      `${apiClient.defaults.baseURL}/auth/refresh`,
      {},
      { withCredentials: true },
    )
    .then((r) => {
      setAccessToken(r.data.access_token)
      return r.data.access_token
    })
    .catch(() => {
      setAccessToken(null)
      onSessionLost()
      return null
    })
    .finally(() => {
      refreshInFlight = null
    })

  refreshInFlight = promise
  return promise
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined

    if (!original || error.response?.status !== 401 || original._retried) {
      return Promise.reject(normalizeError(error))
    }

    if (original.url?.includes('/auth/refresh') || original.url?.includes('/auth/login')) {
      return Promise.reject(normalizeError(error))
    }

    original._retried = true
    const newToken = await refreshAccessToken()
    if (!newToken) return Promise.reject(normalizeError(error))

    original.headers.Authorization = `Bearer ${newToken}`
    return apiClient.request(original)
  },
)

export interface ApiError {
  code: string
  message: string
  status: number
  details?: unknown
}

function apiUrl(path: string): string {
  const base = String(apiClient.defaults.baseURL ?? '/api').replace(/\/$/, '')
  return `${base}/${path.replace(/^\//, '')}`
}

/** Authenticated Fetch transport for true browser ReadableStream endpoints.
 *
 * Axios remains the default API client. Streaming chat uses this helper because
 * XHR download progress can close a chunked response before its terminal event.
 */
export async function authenticatedFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  const request = async (token: string | null) => {
    const headers = new Headers(init.headers)
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
    if (token) headers.set('Authorization', `Bearer ${token}`)
    const clientId = getCurrentClientId()
    if (clientId && needsClientHeader(path)) headers.set('X-Client-Id', clientId)
    return fetch(apiUrl(path), {
      ...init,
      headers,
      credentials: 'include',
    })
  }

  let response = await request(getAccessToken())
  if (response.status === 401 && !path.includes('/auth/')) {
    const token = await refreshAccessToken()
    if (token) response = await request(token)
  }
  if (response.ok) return response

  let body: { error?: { code?: string; message?: string; details?: unknown } } | undefined
  try {
    body = (await response.json()) as typeof body
  } catch {
    body = undefined
  }
  throw {
    status: response.status,
    code: body?.error?.code ?? 'network_error',
    message: body?.error?.message ?? `Request failed with status ${response.status}`,
    details: body?.error?.details,
  } satisfies ApiError
}

function normalizeError(err: AxiosError): ApiError {
  const status = err.response?.status ?? 0
  const body = err.response?.data as
    | { error?: { code?: string; message?: string; details?: unknown } }
    | undefined
  return {
    status,
    code: body?.error?.code ?? (err.code === 'ECONNABORTED' ? 'request_timeout' : 'network_error'),
    message:
      body?.error?.message ??
      (err.code === 'ECONNABORTED'
        ? 'คำขอใช้เวลานานเกินกำหนด งานอาจยังทำงานอยู่ กรุณารอสักครู่แล้วตรวจสอบอีกครั้ง'
        : err.message || 'Unknown error'),
    details: body?.error?.details,
  }
}
